import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionRow, Button, Form, ModalDialog,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';

import { useInsertTicket } from '../../../gql';
import { useOrgId } from '../../../gql/hooks/useOrgId';
import messages from '../messages';
import { TICKET_TYPES } from '../utils';

const TYPE_PLACEHOLDER_KEYS = {
  Issue: 'issuePlaceholder',
  Bug: 'bugPlaceholder',
  Feature: 'featurePlaceholder',
  Other: 'otherPlaceholder',
};

const TYPE_LABEL_KEYS = {
  Issue: 'issueType',
  Bug: 'bugType',
  Feature: 'featureType',
  Other: 'otherType',
};

const RaiseTicketModal = ({
  isOpen, onClose, onCreated, showToast,
}) => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const orgId = useOrgId();

  const [title, setTitle] = useState('');
  const [type, setType] = useState('Issue');
  const [description, setDescription] = useState('');
  const [titleError, setTitleError] = useState('');

  const { insertTicket, loading } = useInsertTicket();

  const reset = () => {
    setTitle('');
    setType('Issue');
    setDescription('');
    setTitleError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      setTitleError(intl.formatMessage(messages.ticketTitleRequired));
      return;
    }

    const userId = authenticatedUser?.userId ?? authenticatedUser?.id;

    insertTicket({
      variables: {
        title: title.trim(),
        description: description.trim(),
        type,
        raisedBy: userId,
        organisationId: orgId,
        ticketCategory: 'learner_support',
      },
      onCompleted: () => {
        showToast(intl.formatMessage(messages.ticketAddedSuccess), 'success');
        onCreated?.();
        handleClose();
      },
      onError: () => {
        showToast(intl.formatMessage(messages.somethingWentWrong), 'error');
      },
    });
  };

  const modalTitle = intl.formatMessage(messages.modalTitle);

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={handleClose}
      hasCloseButton
      size="md"
      title={modalTitle}
    >
      <ModalDialog.Header>
        <ModalDialog.Title>{modalTitle}</ModalDialog.Title>
        <p className="learner-support__modal-subtitle">
          {intl.formatMessage(messages.modalSubtitle)}
        </p>
      </ModalDialog.Header>
      <ModalDialog.Body>
        <div className="learner-support__modal-form">
          <Form.Group size="lg" isInvalid={!!titleError}>
            <Form.Label>{intl.formatMessage(messages.ticketTitleLabel)}</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError) { setTitleError(''); }
              }}
              placeholder={intl.formatMessage(messages.ticketTitlePlaceholder)}
              disabled={loading}
            />
            {titleError && (
              <Form.Control.Feedback type="invalid">{titleError}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group size="lg">
            <Form.Label>{intl.formatMessage(messages.problemTypeLabel)}</Form.Label>
            <Form.Control
              as="select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={loading}
            >
              {TICKET_TYPES.map((option) => (
                <option key={option} value={option}>
                  {intl.formatMessage(messages[TYPE_LABEL_KEYS[option]])}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group size="lg">
            <Form.Label>{intl.formatMessage(messages.ticketDescriptionLabel)}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage(messages[TYPE_PLACEHOLDER_KEYS[type]])}
              disabled={loading}
            />
          </Form.Group>
        </div>
      </ModalDialog.Body>
      <ModalDialog.Footer>
        <ActionRow>
          <Button variant="tertiary" size="lg" onClick={handleClose} disabled={loading}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button variant="primary" size="lg" onClick={handleSubmit} disabled={loading}>
            {intl.formatMessage(messages.raiseTicket)}
          </Button>
        </ActionRow>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

RaiseTicketModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  showToast: PropTypes.func.isRequired,
};

RaiseTicketModal.defaultProps = {
  onCreated: undefined,
};

export default RaiseTicketModal;
